/* Community videos — sections/ref-community-videos.liquid
   Videos are the store's own Shopify-hosted files and the products are real
   store products (prices, variants and stock come from the page). ADD TO CART
   posts to /cart/add.js, so the store header shows its own confirmation. */
(() => {
  if (window.__rcvReady) return;
  window.__rcvReady = true;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const HASH = '#video-shopping';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* phones: the viewer is a full-screen vertical feed that scrolls like reels */
  const feedQuery = window.matchMedia('(max-width: 767px)');
  const isFeed = () => feedQuery.matches;

  const icon = {
    close: '<svg viewBox="0 0 20 20"><path d="M5 5l10 10M15 5 5 15"/></svg>',
    soundOn: '<svg viewBox="0 0 24 24"><path d="M4 9h4l5-4v14l-5-4H4z"/><path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12"/></svg>',
    soundOff: '<svg viewBox="0 0 24 24"><path d="M4 9h4l5-4v14l-5-4H4z"/><path d="m17 9 5 6M22 9l-5 6"/></svg>',
    share: '<svg viewBox="0 0 24 24"><circle cx="18" cy="5" r="2.5"/><circle cx="6" cy="12" r="2.5"/><circle cx="18" cy="19" r="2.5"/><path d="m8.2 10.8 7.6-4.4M8.2 13.2l7.6 4.4"/></svg>',
    go: '<svg viewBox="0 0 24 24"><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/></svg>',
    play: '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>',
  };

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const addToCart = async (id) => {
    const res = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.description || body.message || 'Could not add this item.');
    if (!document.querySelector('.ref-header')) {
      ['cart:refresh', 'cart:updated'].forEach((n) => document.dispatchEvent(new CustomEvent(n, { detail: { item: body } })));
    }
    return body;
  };

  const init = (root) => {
    if (!root || root.dataset.rcvInit === '1') return;
    root.dataset.rcvInit = '1';

    const track = root.querySelector('[data-rcv-track]');
    const view = root.querySelector('[data-rcv-view]');
    if (!track || !view) return;
    document.body.appendChild(view); /* fixed layer above the whole page */
    root._rcvView = view;

    const slides = [...track.querySelectorAll('[data-rcv-slide]')];
    const data = slides.map((slide) => {
      try { return JSON.parse(slide.querySelector('[data-rcv-data]').textContent); } catch (_) { return { src: '', poster: '', products: [] }; }
    });
    const autoplay = root.dataset.autoplay !== 'false' && !reduced;

    /* ---------- row videos: play silently only while on screen ---------- */
    const cardVideo = (slide) => slide.querySelector('.rcv__video');
    const start = (video) => {
      if (!video.src && video.dataset.src) video.src = video.dataset.src;
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    };
    let viewerOpen = false;
    if (autoplay && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          const video = cardVideo(en.target);
          if (!video) return;
          /* keep playing behind the desktop viewer, like the reference */
          if (en.isIntersecting && !(viewerOpen && isFeed())) start(video);
          else video.pause();
        });
      }, { threshold: 0.35 });
      slides.forEach((s) => io.observe(s));
      root._rcvIo = io;
    } else {
      slides.forEach((s) => {
        const video = cardVideo(s);
        s.addEventListener('mouseenter', () => start(video));
        s.addEventListener('mouseleave', () => video.pause());
      });
    }

    /* ---------- row arrows (wrap round at the ends) ---------- */
    const step = () => {
      const first = slides[0];
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      return first.getBoundingClientRect().width + gap;
    };
    root.querySelector('[data-rcv-next]')?.addEventListener('click', () => {
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      if (atEnd) track.scrollTo({ left: 0 });
      else track.scrollBy({ left: step() });
    });
    root.querySelector('[data-rcv-prev]')?.addEventListener('click', () => {
      if (track.scrollLeft <= 4) track.scrollTo({ left: track.scrollWidth });
      else track.scrollBy({ left: -step() });
    });

    /* ---------- viewer ---------- */
    const stage = view.querySelector('[data-rcv-stage]');
    const count = slides.length;
    let current = 0;
    let muted = false;
    let pushed = false;
    let items = [];
    let player = null;

    let feedIo = null;
    const buildItems = () => {
      if (items.length) return;
      items = data.map((d, i) => {
        const item = document.createElement('div');
        item.className = 'rcv-item';
        item.dataset.i = String(i);
        if (d.poster) {
          const img = document.createElement('img');
          img.className = 'rcv-item__poster';
          img.src = d.poster;
          img.alt = '';
          img.loading = 'lazy';
          item.appendChild(img);
        }
        stage.appendChild(item);
        return item;
      });
      /* feed: the video that fills the screen after a scroll becomes the current one */
      if ('IntersectionObserver' in window) {
        feedIo = new IntersectionObserver((entries) => {
          if (!viewerOpen || !isFeed()) return;
          entries.forEach((en) => {
            if (!en.isIntersecting || en.intersectionRatio < 0.6) return;
            const i = Number(en.target.dataset.i);
            if (i !== current || !player) activate(i);
          });
        }, { root: stage, threshold: [0.6] });
        items.forEach((item) => feedIo.observe(item));
        root._rcvFeedIo = feedIo;
      }
    };

    const offset = (i) => {
      let d = i - current;
      if (count >= 5) { /* circular, like the reference */
        if (d > count / 2) d -= count;
        if (d < -count / 2) d += count;
      }
      return d;
    };

    const productCard = (p) => {
      const off = p.off > 0 ? `<span class="rcv-prod__was">${esc(p.compare)}</span><span class="rcv-prod__off">${p.off}% OFF</span>` : '';
      return `<div class="rcv-prod" data-rcv-prod>
        <div class="rcv-prod__row">
          <a class="rcv-prod__img" href="${esc(p.url)}">${p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy">` : ''}</a>
          <div class="rcv-prod__info">
            <a class="rcv-prod__title" href="${esc(p.url)}">${esc(p.title)}</a>
            <p class="rcv-prod__price"><span>${esc(p.price)}</span>${off}</p>
          </div>
          <a class="rcv-prod__go" href="${esc(p.url)}" aria-label="Open ${esc(p.title)}">${icon.go}</a>
        </div>
        <button class="rcv-prod__add" type="button" data-rcv-add ${p.available ? '' : 'disabled'}>${p.available ? 'Add to cart' : 'Sold out'}</button>
        <div class="rcv-prod__sizes" data-rcv-sizes hidden></div>
        <p class="rcv-prod__msg" data-rcv-msg hidden></p>
      </div>`;
    };

    const note = (item, text) => {
      const n = item.querySelector('.rcv-item__note');
      if (!n) return;
      n.textContent = text;
      n.classList.add('is-show');
      clearTimeout(n._t);
      n._t = setTimeout(() => n.classList.remove('is-show'), 1800);
    };

    const mountPlayer = (item, d) => {
      if (player) player.remove();
      item.classList.remove('is-paused');
      player = document.createElement('div');
      player.className = 'rcv-item__ui';
      player.innerHTML = `
        <video class="rcv-item__video" playsinline preload="auto" ${d.poster ? `poster="${esc(d.poster)}"` : ''}></video>
        <div class="rcv-item__bar"><span></span></div>
        <div class="rcv-item__top">
          <button class="rcv-ctl" type="button" data-rcv-mute aria-label="${muted ? 'Sound on' : 'Sound off'}">${muted ? icon.soundOff : icon.soundOn}</button>
          <button class="rcv-ctl" type="button" data-rcv-close aria-label="Close">${icon.close}</button>
        </div>
        <div class="rcv-item__side">
          <button class="rcv-ctl" type="button" data-rcv-share aria-label="Share">${icon.share}</button>
        </div>
        <span class="rcv-item__note" role="status"></span>
        <span class="rcv-item__paused" aria-hidden="true">${icon.play}</span>
        ${d.products.length ? `<div class="rcv-item__shop">${d.products.map(productCard).join('')}</div>` : ''}`;
      item.appendChild(player);
      const video = player.querySelector('video');
      const bar = player.querySelector('.rcv-item__bar span');
      video.muted = muted;
      if (d.src) video.src = d.src;
      video.addEventListener('timeupdate', () => {
        if (video.duration) bar.style.width = `${(video.currentTime / video.duration) * 100}%`;
      });
      video.addEventListener('ended', () => go(current + 1));
      video.addEventListener('click', () => {
        if (video.paused) { video.play().catch(() => {}); item.classList.remove('is-paused'); }
        else { video.pause(); item.classList.add('is-paused'); }
      });
      const p = video.play();
      if (p && p.catch) p.catch(() => { /* sound not allowed yet: play silently */
        muted = true;
        video.muted = true;
        const b = player.querySelector('[data-rcv-mute]');
        if (b) { b.innerHTML = icon.soundOff; b.setAttribute('aria-label', 'Sound on'); }
        video.play().catch(() => item.classList.add('is-paused'));
      });
    };

    const layout = () => {
      items.forEach((item, i) => {
        const d = offset(i);
        item.dataset.pos = Math.abs(d) <= 2 ? String(d) : (d < 0 ? 'far-left' : 'far-right');
        item.setAttribute('aria-hidden', d === 0 ? 'false' : 'true');
      });
      mountPlayer(items[current], data[current]);
    };

    const activate = (i) => {
      current = i;
      layout();
    };

    const go = (i, instant) => {
      if (!count) return;
      const next = ((i % count) + count) % count;
      if (isFeed() && items[next]) {
        stage.scrollTo({ top: items[next].offsetTop, behavior: instant || reduced ? 'auto' : 'smooth' });
        if (instant || !feedIo) activate(next);
        return;
      }
      activate(next);
    };

    const open = (i) => {
      buildItems();
      viewerOpen = true;
      if (isFeed()) slides.forEach((s) => cardVideo(s)?.pause());
      view.hidden = false;
      view.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('rcv-lock');
      requestAnimationFrame(() => view.classList.add('is-open'));
      if (location.hash !== HASH) {
        history.pushState({ rcv: true }, '', HASH);
        pushed = true;
      }
      go(i, true);
    };

    const close = (fromHistory) => {
      if (!viewerOpen) return;
      viewerOpen = false;
      view.classList.remove('is-open');
      view.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('rcv-lock');
      if (player) { player.querySelector('video')?.pause(); player.remove(); player = null; }
      setTimeout(() => { if (!viewerOpen) view.hidden = true; }, 300);
      if (pushed && !fromHistory) history.back();
      pushed = false;
      /* the row starts playing again where it is visible */
      slides.forEach((s) => {
        const r = s.getBoundingClientRect();
        if (autoplay && r.right > 0 && r.left < innerWidth && r.bottom > 0 && r.top < innerHeight) start(cardVideo(s));
      });
    };

    track.addEventListener('click', (e) => {
      const card = e.target.closest('[data-rcv-open]');
      if (card) open(Number(card.dataset.rcvOpen));
    });

    view.addEventListener('click', async (e) => {
      if (e.target.closest('[data-rcv-close]') || e.target === stage) { close(false); return; }
      const stepBtn = e.target.closest('[data-rcv-step]');
      if (stepBtn) { go(current + Number(stepBtn.dataset.rcvStep)); return; }
      const side = e.target.closest('.rcv-item');
      if (side && side.dataset.pos !== '0') { go(Number(side.dataset.i)); return; }

      if (e.target.closest('[data-rcv-mute]')) {
        muted = !muted;
        const video = player?.querySelector('video');
        if (video) { video.muted = muted; if (!muted) video.play().catch(() => {}); }
        const b = e.target.closest('[data-rcv-mute]');
        b.innerHTML = muted ? icon.soundOff : icon.soundOn;
        b.setAttribute('aria-label', muted ? 'Sound on' : 'Sound off');
        return;
      }
      if (e.target.closest('[data-rcv-share]')) {
        const url = `${location.origin}${location.pathname}`;
        try {
          if (navigator.share) await navigator.share({ title: document.title, url });
          else { await navigator.clipboard.writeText(url); note(items[current], 'Link copied'); }
        } catch (_) {}
        return;
      }

      const add = e.target.closest('[data-rcv-add]');
      const size = e.target.closest('.rcv-size');
      const card = e.target.closest('[data-rcv-prod]');
      if (!card || (!add && !size)) return;
      const cards = [...player.querySelectorAll('[data-rcv-prod]')];
      const product = data[current].products[cards.indexOf(card)];
      const msg = card.querySelector('[data-rcv-msg]');
      const sizes = card.querySelector('[data-rcv-sizes]');
      const addBtn = card.querySelector('[data-rcv-add]');
      const done = async (variantId, button) => {
        msg.hidden = true;
        button.disabled = true;
        try {
          await addToCart(variantId);
          sizes.hidden = true;
          addBtn.hidden = false;
          addBtn.textContent = 'Added';
          setTimeout(() => { addBtn.textContent = 'Add to cart'; }, 1600);
        } catch (err) {
          msg.textContent = err.message;
          msg.hidden = false;
        } finally {
          button.disabled = false;
        }
      };
      if (add) {
        if (product.single || product.variants.length === 1) { done(product.variants[0].id, add); return; }
        /* several sizes: show them, like the product page does */
        sizes.innerHTML = product.variants.map((v) => `<button class="rcv-size" type="button" data-variant="${v.id}" ${v.available ? '' : 'disabled'}>${esc(v.title)}</button>`).join('');
        sizes.hidden = false;
        add.hidden = true;
        return;
      }
      if (size && !size.disabled) done(size.dataset.variant, size);
    });

    /* swipe between videos on touch screens */
    let sx = 0; let sy = 0;
    stage.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      if (isFeed() || e.target.closest('.rcv-item__shop')) return;
      const dx = e.changedTouches[0].clientX - sx;
      const dy = e.changedTouches[0].clientY - sy;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) go(current + (dx < 0 ? 1 : -1));
    }, { passive: true });

    document.addEventListener('keydown', (e) => {
      if (!viewerOpen) return;
      if (e.key === 'Escape') close(false);
      else if (e.key === 'ArrowRight' || (isFeed() && e.key === 'ArrowDown')) { e.preventDefault(); go(current + 1); }
      else if (e.key === 'ArrowLeft' || (isFeed() && e.key === 'ArrowUp')) { e.preventDefault(); go(current - 1); }
    });
    window.addEventListener('popstate', () => { if (viewerOpen && location.hash !== HASH) close(true); });
  };

  document.querySelectorAll('[data-rcv]').forEach(init);
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-rcv]').forEach(init));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-rcv]').forEach((root) => {
      root._rcvIo?.disconnect();
      root._rcvFeedIo?.disconnect();
      root._rcvView?.remove();
      document.documentElement.classList.remove('rcv-lock');
    });
  });
})();
